import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, download, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { CrudPage } from "@/components/CrudPage";
import { DataTable, DemoBadge, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ArrowLeft } from "lucide-react";

const CUSTOMER_FIELDS = [
  { key: "name", label: "Full Name", required: true },
  { key: "company", label: "Company Name" },
  { key: "customer_type", label: "Customer Type", type: "select",
    options: [{ value: "individual", label: "Individual" }, { value: "business", label: "Business / Corporate" }] },
  { key: "gst_treatment", label: "GST Treatment", type: "select",
    options: [{ value: "b2c", label: "B2C (no GSTIN)" }, { value: "b2b", label: "B2B (registered)" }] },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "gstin", label: "GSTIN", hint: "15-character GSTIN, e.g. 29ABCDE1234F1Z5" },
  { key: "pan", label: "PAN" },
  { key: "address", label: "Address", full: true },
  { key: "city", label: "City" },
  { key: "state", label: "State", type: "state" },
  { key: "pin", label: "PIN Code" },
  { key: "place_of_supply", label: "Default Place of Supply", hint: "Defaults to customer state" },
  { key: "opening_balance", label: "Opening Balance (₹)", type: "number" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

const CUSTOMER_COLUMNS = [
  { key: "customer_id", label: "ID" },
  { key: "name", label: "Name", render: (r) => (
    <div><span className="font-medium">{r.name}</span>{r.is_demo && <DemoBadge />}
      {r.company && <div className="text-xs text-[#5C6B69]">{r.company}</div>}</div>) },
  { key: "gst_treatment", label: "Type", render: (r) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.gst_treatment === "b2b" ? "bg-[#0D4F4A]/10 text-[#0D4F4A]" : "bg-[#C9A227]/15 text-[#8a6d14]"}`}>
      {r.gst_treatment?.toUpperCase()}</span>) },
  { key: "phone", label: "Phone" },
  { key: "state", label: "State" },
  { key: "gstin", label: "GSTIN", render: (r) => r.gstin || <span className="text-[#5C6B69] text-xs">—</span> },
];

export function Customers() {
  const navigate = useNavigate();
  return (
    <div>
      <CrudPage title="Customers" subtitle="Customer database with GSTIN, ledgers & history"
        testid="customers" endpoint="/customers" searchPlaceholder="Search name, phone, GSTIN…"
        createLabel="New Customer" fields={CUSTOMER_FIELDS} columns={CUSTOMER_COLUMNS}
        defaults={{ name: "", company: "", customer_type: "individual", gst_treatment: "b2c",
          phone: "", email: "", address: "", city: "", state: "Karnataka", state_code: "29",
          pin: "", gstin: "", pan: "", place_of_supply: "", opening_balance: 0, notes: "" }}
        onRow={(r) => navigate(`/customers/${r.id}`)} />
      <div className="mt-3">
        <Button variant="outline" size="sm" onClick={() => download("/customers/export", "customers.csv")}
          data-testid="customers-export-csv">Export Customers (CSV)</Button>
      </div>
    </div>
  );
}

export function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/customers/${id}/ledger`).then((r) => setData(r.data)).catch((e) => {
      toast.error(errMsg(e));
      navigate("/customers");
    });
  }, [id, navigate]);
  useEffect(load, [load]);
  if (!data) return <div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-[#5C6B69]" /></div>;
  const { customer: c, totals, invoices, credit_notes, payments, bookings } = data;
  return (
    <div data-testid="customer-detail-page">
      <PageHeader title={<span className="flex items-center gap-2">{c.name} {c.is_demo && <DemoBadge />}</span>}
        subtitle={`${c.customer_id} · ${c.gst_treatment?.toUpperCase()} · ${c.city || ""} ${c.state || ""}`}
        testid="customer-detail-header"
        actions={<Button variant="outline" onClick={() => navigate("/customers")} data-testid="customer-detail-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[["Total Billed", totals.billed], ["Credit Notes", totals.credit_notes], ["Total Paid", totals.paid], ["Outstanding", totals.outstanding]].map(([l, v], i) => (
          <div key={l} className="bg-white border border-[#E5E0D8] rounded-xl p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69]">{l}</div>
            <div className={`num text-xl font-bold mt-1 ${i === 3 ? "text-red-600" : "text-[#0D4F4A]"}`} data-testid={`customer-total-${i}`}>{inr(v)}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5 mb-6 text-sm grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><span className="text-[#5C6B69]">GSTIN:</span> {c.gstin || "—"}</div>
        <div><span className="text-[#5C6B69]">PAN:</span> {c.pan || "—"}</div>
        <div><span className="text-[#5C6B69]">Phone:</span> {c.phone || "—"}</div>
        <div><span className="text-[#5C6B69]">Email:</span> {c.email || "—"}</div>
        <div className="col-span-2"><span className="text-[#5C6B69]">Address:</span> {[c.address, c.city, c.state, c.pin].filter(Boolean).join(", ") || "—"}</div>
        <div><span className="text-[#5C6B69]">State Code:</span> {c.state_code || "—"}</div>
        <div><span className="text-[#5C6B69]">Place of Supply:</span> {c.place_of_supply || "—"}</div>
      </div>
      <Tabs defaultValue="invoices">
        <TabsList data-testid="customer-detail-tabs">
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
          <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="notes">Credit Notes ({credit_notes.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4">
          <DataTable testid="customer-invoices" columns={[
            { key: "invoice_no", label: "Invoice", render: (r) => <span className="font-semibold text-[#0D4F4A]">{r.invoice_no}</span> },
            { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
            { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} /> },
            { key: "balance", label: "Balance", align: "right", render: (r) => <Money value={r.balance} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          ]} rows={invoices} onRow={(r) => navigate(`/invoices/${r.id}`)} empty="No invoices" />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <DataTable testid="customer-payments" columns={[
            { key: "receipt_no", label: "Receipt" }, { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
            { key: "invoice_no", label: "Invoice", render: (r) => r.invoice_no || "Advance" },
            { key: "mode", label: "Mode" },
            { key: "amount", label: "Amount", align: "right", render: (r) => <Money value={r.amount} /> },
          ]} rows={payments} empty="No payments" />
        </TabsContent>
        <TabsContent value="bookings" className="mt-4">
          <DataTable testid="customer-bookings" columns={[
            { key: "booking_id", label: "ID" }, { key: "package_name", label: "Package" },
            { key: "start_date", label: "Travel", render: (r) => fmtDate(r.start_date) },
            { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          ]} rows={bookings} empty="No bookings" />
        </TabsContent>
        <TabsContent value="notes" className="mt-4">
          <DataTable testid="customer-credit-notes" columns={[
            { key: "invoice_no", label: "Credit Note" }, { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
            { key: "reason", label: "Reason" },
            { key: "grand_total", label: "Amount", align: "right", render: (r) => <Money value={r.grand_total} /> },
          ]} rows={credit_notes} onRow={(r) => navigate(`/invoices/${r.id}`)} empty="No credit notes" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
